/**
 * Accueil (`/extranet/:tenantSlug`, design `accueil`, fiches 22062 / 22058,
 * 22065, 22067, 22079, 22080): today's date, « Bienvenue, {prénom} », the
 * subtitle, then the pending-pieces banner (`extranet.pendingPiecesBanner`)
 * and the counter tiles (`extranet.homeQuickAccess`).
 *
 * The page header is the design's own welcome block (no breadcrumb band).
 * The body is wrapped in a `RuleContextProvider` exposing the base extranet
 * context (tenant, VEOS environment, session) for conditional rules.
 */

import { useMemo } from 'react';

import { Text } from '@components/base/display';
import { VStack } from '@components/base/layout';
import { RuleContextProvider } from '@components/shared/configurable';

import { useExtranetAuthStore } from '../auth/store';
import { formatHomeDate, HOME_TEXT, welcomeNameOf } from '../components/home/homeText';
import {
  PendingPiecesBannerConfigurable as PendingPiecesBanner,
  QuickAccessTilesConfigurable as QuickAccessTiles,
} from '../components/home/register';
import { useExtranetBaseRuleContext } from '../contextVariables';

export function HomePage() {
  const person = useExtranetAuthStore((s) => s.person);
  const ruleContext = useExtranetBaseRuleContext();

  const today = useMemo(() => formatHomeDate(new Date()), []);
  const name = welcomeNameOf(person);

  return (
    <div data-testid="extranet-page-home">
      <RuleContextProvider value={ruleContext}>
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
          <VStack gap={8}>
            <VStack gap={2}>
              <Text
                size="xs"
                color="muted"
                className="uppercase tracking-widest"
                data-testid="extranet-home-date"
              >
                {today}
              </Text>
              <Text as="h1" size="3xl" weight="semibold" color="default">
                {HOME_TEXT.welcome}
                {name !== '' && (
                  <>
                    ,{' '}
                    <Text as="span" size="3xl" weight="semibold" color="primary">
                      {name}
                    </Text>
                  </>
                )}
              </Text>
              <Text size="sm" color="muted">
                {HOME_TEXT.subtitle}
              </Text>
            </VStack>

            <PendingPiecesBanner />
            <QuickAccessTiles />
          </VStack>
        </div>
      </RuleContextProvider>
    </div>
  );
}
